import { view } from '@yola/ws-sdk';
import traverse from 'traverse';
import React from 'react';
import { APPEARANCE_TAB_ID } from '../constants/common';
import { decorativeOptionTypes } from '../constants/option-types';
import getOptionNestedAttributes from './get-option-nested-attributes';
import getOptionTargetElementIds from './get-option-target-element-ids';

const processOptionsRecursively = (elementId, settingId, options, isSubOption = false) =>
  options.map((option) => {
    if (decorativeOptionTypes.includes(option.type)) {
      return option;
    }

    const newOption = {
      ...option,
      settingId,
      elementIds: getOptionTargetElementIds(elementId, option),
      isSubOption,
    };

    let component = null;
    if (option.component) {
      component = React.cloneElement(option.component, {
        elementId,
        option: newOption,
        settingId,
      });
    }

    if (option.options) {
      newOption.attributes = getOptionNestedAttributes(option);
      newOption.options = processOptionsRecursively(elementId, settingId, option.options, true);
    }

    return {
      ...newOption,
      ...(component && {
        component,
      }),
    };
  });

const prepareElementSettings = (elements, elementSettings) =>
  elements.reduce((settingsMap, element) => {
    const matchedSettings = elementSettings.filter((setting) => {
      const isSettingAlreadyPrepared = settingsMap.some(({ id }) => id === setting.id);
      const isSettingMatches = setting.matches(element, setting.settings);

      return isSettingMatches && !isSettingAlreadyPrepared;
    });

    if (!matchedSettings) return settingsMap;

    matchedSettings
      .sort((a, b) => b.priority - a.priority)
      .forEach((setting) => {
        const { settings } = setting;
        let targetElement = element;

        if (setting.getTargetElement) {
          targetElement = setting.getTargetElement(targetElement, settings);
        }

        const elementId = view.accessors.getLiveElementId(targetElement);
        if (!elementId) return;

        const preparedTabs = setting.tabs.map((tab) => ({
          ...tab,
          id: `${setting.id}-${tab.id}`,
          settingId: setting.id,
          elementId: setting.elementId,
        }));

        const preparedSettings = {
          priority: 0,
          ...setting,
          tabs: preparedTabs,
          elementId,
        };

        // eslint-disable-next-line func-names
        traverse(preparedSettings).forEach(function (val) {
          if (val?.id?.includes(APPEARANCE_TAB_ID)) {
            const newOpts = processOptionsRecursively(elementId, val.settingId, val.options);

            this.update({
              ...val,
              options: newOpts,
            });
          }

          if (React.isValidElement(val)) {
            if (!val.props.elementId) {
              // eslint-disable-next-line yola/react/no-this-in-sfc
              this.update(
                React.cloneElement(val, {
                  elementId,
                })
              );
            }
          }
        });

        settingsMap.push(preparedSettings);
      });

    return settingsMap;
  }, []);

export default prepareElementSettings;
